import {
    Button as MuiButton,
    DialogActions,
    DialogTitle,
    Radio
} from "@material-ui/core";
import React, { Component } from "react";
import styled, { css } from "styled-components";

import { player as playerApi, presentations as presentationsApi } from "apis/callables";
import { PdfCompressionTypes } from "common/constants";
import { ActionPermissionsObject, WorkspacePlanTier } from "common/interfaces";
import WorkspaceController from "js/controllers/WorkspaceController";
import getLogger, { LogGroup } from "js/core/logger";
import { PresentationPrivacyType } from "js/core/models/presentation";
import { fontManager } from "js/core/services/fonts";
import { download, trackActivity } from "js/core/utilities/utilities";
import { t } from "js/i18n";
import { app } from "js/namespaces";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";
import { exportToGoogleSlides } from "js/exporter/exportToGoogleSlides";
import { Button } from "js/Components/Button";
import { Icon } from "js/Components/Icon";
import Banner from "js/Components/Banner";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import CheckBox from "js/react/components/CheckBox";
import {
    BeautifulDialog,
    DialogContent,
    ShowConfirmationDialog,
    ShowDialog,
    ShowMessageDialog,
    ShowSnackBar
} from "js/react/components/Dialogs/BaseDialog";
import ProgressDialog from "js/react/components/Dialogs/ProgressDialog";
import { FlexBox } from "js/react/components/LayoutGrid";
import { themeColors } from "js/react/sharedStyles";

import linkDS from "../dataservices/LinkDataService";
import { copyShareLink } from "./ShareDialogCopy";

const logger = getLogger(LogGroup.EXPORT);

const DialogTitleShort = styled(DialogTitle)`
    && {
        padding: 40px 40px 25px;
    }
`;

const DialogContentNoPad = styled(DialogContent)`
    && {
        display: flex;
        flex-direction: column;
        gap: 25px;
        padding: 0 40px;
    }
`;

const DialogActionsPadded = styled(DialogActions)`
    && {
        margin: 0px;
        padding: 20px 40px 40px;
    }
`;

const InfoText = styled.span`
    color: #666;
    font-family: "Source Sans Pro";
    font-size: 12px;
    font-weight: 400;
    line-height: 120%;
    text-transform: none;

    b {
        font-weight: 600;
    }
`;

const ShareLinkBox = styled.div`
    display: flex;
    align-items: center;
    gap: 50px;
    border: 1px solid ${themeColors.selection};
    border-radius: 2px;
    padding: 10px 15px;

    .share-link-text {
        flex: 1;
    }

    .share-link-title {
        font-size: 14px;
        font-weight: 600;
        color: #555;
    }

    .share-link-subtitle {
        margin-top: 2px;
        color: #666;
        font-size: 12px;
        font-weight: 400;
        line-height: 120%;
    }
`;

const ExportOptions = styled.div`
    font-size: 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const OptionCard = styled.div`
    background: ${props => props.$selected ? "#F5FBFF" : "#F7F8F9"};
    border: 1px solid ${props => props.$selected ? themeColors.ui_blue : "transparent"};
    border-radius: 2px;
    padding: 10px 12px;
    cursor: pointer;
    transition: background 150ms ease, border-color 150ms ease;

    &:hover {
        background: ${props => props.$selected ? "#F5FBFF" : "#EFF1F2"};
    }
`;

const OptionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const OptionLabel = styled.div`
    font-weight: bold;
    font-size: 14px;
    color: #555;
    opacity: ${props => props.$blocked ? 0.5 : 1};
`;

const OptionBody = styled.div`
    ${props => props.$divided ? css`
        margin-top: 10px;
        padding-top: 10px;
        border-top: 1px solid ${themeColors.lightGray};
    ` : css`
        padding-left: 32px;
    `}
`;

const CheckBoxContainer = styled.div`
    .MuiFormControlLabel-root {
        margin-left: 0;
    }

    .MuiCheckbox-root {
        padding: 0 8px 0 0 !important;
    }

    .MuiCheckbox-root:hover {
        background-color: transparent;
    }

    .MuiCheckbox-root .MuiSvgIcon-root {
        font-size: 18px;
    }

    .MuiCheckbox-root.Mui-checked {
        color: ${themeColors.ui_blue};
    }
`;

const RadioSmall = styled(Radio).attrs({ disableRipple: true })`
    && {
        padding: 0 8px 0 0;
    }

    &&:hover {
        background-color: transparent;
    }

    && .MuiSvgIcon-root {
        font-size: 18px;
    }

    &&.Mui-checked {
        color: ${themeColors.ui_blue};
    }
`;

const StackedOptions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const IncludeSkippedContainer = styled.div`
    padding: 4px 0 0;

    .MuiFormControlLabel-root {
        margin-left: 0;
    }

    .MuiCheckbox-root {
        padding: 0 8px 0 0 !important;
    }

    .MuiCheckbox-root:hover {
        background-color: transparent;
    }

    .MuiCheckbox-root .MuiSvgIcon-root {
        font-size: 18px;
    }

    .MuiCheckbox-root.Mui-checked {
        color: ${themeColors.ui_blue};
    }
`;

const ExportUpgradeBanner = styled(Banner)`
    &&& {
        background: #F7FAFC;
        border-color: ${themeColors.lightBlue};
        padding: 10px 15px;

        .bai-button {
            background: #FFFFFF;
            color: #333333;
        }

        .bai-button span {
            color: #333333;
        }
    }
`;

export default class ExportDialog extends Component {
    constructor(props) {
        super(props);

        this.state = {
            exportType: null,
            // Default PowerPoint export to "images" — users opt into editable explicitly.
            isEditablePptx: false,
            pdfCompressionType: "none",
            includeSkippedSlides: false,
            downloadFonts: true,
            copyingLink: false
        };
    }

    get canExportEditable() {
        return WorkspaceController.actionPermissions[ActionPermissionsObject.EXPORT_PRESENTATION_TO_EDITABLE].use;
    }

    get canExportOffline() {
        return WorkspaceController.actionPermissions[ActionPermissionsObject.DESKTOP_APP].use;
    }

    get canUpgradeToUseExportEditable() {
        return WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.EXPORT_PRESENTATION_TO_EDITABLE, "use");
    }

    get exportAllowed() {
        return WorkspaceController.actionPermissions[ActionPermissionsObject.EXPORT_PRESENTATION].use;
    }

    get shouldShowUpgradeBanner() {
        // Only prompt to upgrade when editable export is hidden because of the current
        // plan (which the user can upgrade away), not because of non-upgradeable team
        // brand guardrails — canUpgradeToUseExportEditable is false in the guardrail case.
        return this.canUpgradeToUseExportEditable;
    }

    showChangePlanDialog = cta => {
        ShowDialog(ChangePlanDialog, {
            featuredPlan: WorkspacePlanTier.PRO,
            analytics: { cta, ...this.props.presentation.getAnalytics() },
        });
    }

    handleCopyLink = async () => {
        const { presentation } = this.props;

        if (this.state.copyingLink) {
            return;
        }

        this.setState({ copyingLink: true });
        try {
            // set the presentation to PUBLIC if necessary
            if (presentation.getPrivacySetting() !== PresentationPrivacyType.PUBLIC) {
                if (!(await ShowConfirmationDialog({
                    title: t("Make this presentation public?"),
                    message: <>
                        <p>{t("In order to share your presentation, its privacy setting must be set to")} <b>{t("Public.")}</b></p>
                        <br />
                        <p>{t("Public presentations can only be viewed by people who have been given a link or invited to the presentation.")}</p>
                    </>
                }))) {
                    return;
                }

                const oldValue = presentation.getPrivacySetting();
                presentation.setPrivacySetting(PresentationPrivacyType.PUBLIC);
                trackActivity("Sharing", "ChangePrivacySetting", null, null, {
                    presentation_id: presentation.get("id"),
                    old_value: oldValue,
                    new_value: PresentationPrivacyType.PUBLIC
                }, { audit: true });
            }

            if (!presentation.links) {
                await presentation.loadPresentationLinks();
            }

            // create a fresh public link, mirroring the mobile "copy link" flow
            const link = await linkDS.createPublicLink(presentation);
            if (!link.get("enabled")) {
                link.update({ enabled: true });
            }
            await linkDS.saveLink(presentation, link);

            const url = link.getLinkURL();
            const analyticsProps = {
                presentation_id: presentation.id,
                link_ids: [link.get("id")],
                is_new: true,
                type: "from_export_menu",
                workspace_id: presentation.getWorkspaceId()
            };

            if (app.isMobileOrTablet && window.navigator.share) {
                await window.navigator.share({ url });
                trackActivity("Sharing", "CopyLink", null, null, analyticsProps, { audit: true });
                return;
            }

            const copyResult = await copyShareLink(url);
            if (copyResult === "copied") {
                ShowSnackBar({ message: t("Share link copied to clipboard") });
                trackActivity("Sharing", "CopyLink", null, null, analyticsProps, { audit: true });
            } else {
                ShowSnackBar({ message: t("Failed to copy link") });
            }
        } catch (err) {
            if (err?.name === "AbortError") {
                return;
            }
            logger.error(err, "[ExportDialog] handleCopyLink() failed", { presentationId: presentation?.id });
            ShowSnackBar({ message: t("Failed to copy link") });
        } finally {
            this.setState({ copyingLink: false });
        }
    }

    handleAccept = async () => {
        const { presentation, closeDialog } = this.props;
        const { includeSkippedSlides = false, exportType, pdfCompressionType, downloadFonts } = this.state;

        if (!exportType) {
            return;
        }

        const isEditablePptx = this.state.isEditablePptx && this.canExportEditable;

        // verify there's anything to export
        if (!includeSkippedSlides) {
            const { models: slides } = presentation.slides;

            let skippedSlides;
            if (slides.some(slide => !slide.loaded)) {
                // some slide models aren't loaded so we have to pull their metadata from the api
                const slidesMetadata = await presentationsApi.getSlidesMetadata({ id: presentation.id });
                skippedSlides = slides.filter(slide => slidesMetadata[slide.id]?.isSkipped);
            } else {
                skippedSlides = slides.filter(slide => presentation.isSlideSkipped(slide));
            }

            const noSlidesExported = skippedSlides.length === slides.length;
            // nothing to export
            if (noSlidesExported) {
                ShowMessageDialog({
                    title: "No Slides to Export",
                    message: "This presentation appears to only contain skipped slides!"
                });
                return;
            }
        }

        // kick off the export
        if (exportType === "powerpoint") {
            if (isEditablePptx) {
                trackActivity("Presentation", "EditablePPTXExport", null, null, {}, { audit: true });

                // Download fonts if toggle is enabled and exporting editable PowerPoint
                if (downloadFonts) {
                    await this.downloadFonts();
                }

                presentation.downloadExportCache("pptx", {
                    destination: "local",
                    includeSkippedSlides,
                    pptxMode: "editable"
                });
            } else {
                presentation.downloadExportCache("pptx", { destination: "local", includeSkippedSlides, pptxMode: "static" });
            }
        } else if (exportType === "pdf") {
            presentation.downloadExportCache("pdf", { destination: "local", includeSkippedSlides, pdfCompressionType });
        } else if (exportType === "offline") {
            presentation.downloadExportCache("zip", { destination: "local", includeSkippedSlides });
        } else if (exportType === "google-slides") {
            exportToGoogleSlides({ includeSkippedSlides });
        }

        closeDialog();
    };

    downloadFonts = async () => {
        if (!this.canExportEditable) {
            return;
        }

        const progressDialog = ShowDialog(ProgressDialog, {
            title: "Exporting fonts..."
        });

        try {
            const canvasControllers = Object.values(PresentationEditorController.getCanvasControllers());

            // Making sure all canvases are rendered to allow fetching their fonts
            for (let canvasController of canvasControllers) {
                if (!canvasController.canvas) {
                    await canvasController.renderCanvas();
                }
            }

            let usedFonts = {};
            const getElementFonts = element => {
                Object.assign(usedFonts, element.fonts);
                Object.values(element.elements || {})
                    .forEach(getElementFonts);
            };
            canvasControllers.forEach(controller => Object.values(controller.canvas.layouter.canvasElement.elements).map(getElementFonts));

            usedFonts = Object.values(usedFonts);
            if (!usedFonts.some(font => font.isBuiltInFont)) {
                ShowMessageDialog({
                    title: "Can't download custom fonts",
                    message: "Your presentation uses custom uploaded fonts which cannot be downloaded from" +
                        " Beautiful.ai. Please install your original copies of these fonts before exporting your" +
                        " presentation."
                });
                return;
            } else if (usedFonts.some(font => font.isCustomFont)) {
                ShowMessageDialog({
                    title: "Can't download custom fonts",
                    message: "Your presentation uses both Beautiful.ai fonts and custom uploaded fonts. Custom uploaded" +
                        " fonts cannot be exported from Beautiful.ai. Please install your original copies of these fonts before exporting your" +
                        " presentation."
                });
            }

            for (const font of usedFonts.filter(font => font.isBuiltInFont)) {
                await fontManager.exportFontPackage(font.id);
            }
        } catch (err) {
            logger.error(err, "ExportDialog downloadFonts() failed to dowload used fonts");
        } finally {
            progressDialog.props.closeDialog();
        }
    }

    downloadOfflinePlayer = async event => {
        event.stopPropagation();

        let os;
        if (window.navigator.platform.indexOf("Win") === 0) {
            os = "windows";
        }
        if (window.navigator.platform.indexOf("Mac") === 0) {
            os = "mac";
        }

        if (!os) {
            ShowMessageDialog({
                title: "Your OS is not supported",
                message: "Sorry, your operating system is not supported, offline player only supports MacOS and Windows"
            });
            return;
        }

        try {
            const { url } = await playerApi.getOfflinePlayerDownloadUrl({ os });
            download(url, `Beautiful.ai Offline Player${os === "windows" ? ".exe" : ".dmg"}`);
        } catch (err) {
            logger.error(err, "ExportDialog downloadOfflinePlayer() failed to dowload offline player", { os });
        }
    }

    handleToggleIncludeSkippedSlides = () => {
        this.setState({ includeSkippedSlides: !this.state.includeSkippedSlides });
    }

    handleSelectPdf = () => {
        this.setState({ exportType: "pdf" });
    }

    handleSelectPowerPoint = () => {
        this.setState({ exportType: "powerpoint" });
    }

    handleSelectGoogleSlides = () => {
        this.setState({ exportType: "google-slides" });
    }

    handleSelectOffline = () => {
        this.setState({ exportType: "offline" });
    }

    handlePdfCompressionToggle = () => {
        this.setState({
            pdfCompressionType: this.state.pdfCompressionType === "none" ? PdfCompressionTypes.HIGH : "none"
        });
    }

    handleSetPptxEditable = editable => {
        this.setState({ isEditablePptx: editable });
    }

    handleToggleDownloadFonts = checked => {
        this.setState({ downloadFonts: checked });
    }

    handleUpgradeBannerClick = () => {
        this.showChangePlanDialog("ExportWarningWatermark");
    }

    renderPdfOption() {
        const { exportType, pdfCompressionType } = this.state;
        const selected = exportType === "pdf";

        return (
            <OptionCard $selected={selected} onClick={this.handleSelectPdf}>
                <OptionHeader>
                    <Icon blue={selected} size={22}>picture_as_pdf</Icon>
                    <OptionLabel>PDF</OptionLabel>
                </OptionHeader>
                {selected && (
                    <OptionBody $divided>
                        <CheckBoxContainer>
                            <CheckBox
                                dataTestId="export-pdf-compression-option"
                                checked={pdfCompressionType !== "none"}
                                onChange={this.handlePdfCompressionToggle}
                                label={<InfoText><b>{t("Compress PDF")}</b>. {t("May result in lower resolution.")}</InfoText>}
                            />
                        </CheckBoxContainer>
                    </OptionBody>
                )}
            </OptionCard>
        );
    }

    renderPowerPointOption() {
        const { exportType, isEditablePptx, downloadFonts } = this.state;
        const selected = exportType === "powerpoint";
        const editableChecked = isEditablePptx && this.canExportEditable;

        return (
            <OptionCard $selected={selected} onClick={this.handleSelectPowerPoint}>
                <OptionHeader>
                    <Icon blue={selected} size={22}>co_present</Icon>
                    <OptionLabel>{t("PowerPoint")}</OptionLabel>
                </OptionHeader>
                {/* Basic plans only support static image export, so there's no mode to choose — show a short note instead of the radios. */}
                {selected && !this.canExportEditable && (
                    <OptionBody $divided>
                        <InfoText>{t("Exports as images.")}</InfoText>
                    </OptionBody>
                )}
                {selected && this.canExportEditable && (
                    <OptionBody $divided>
                        <StackedOptions>
                            <FlexBox
                                data-test-id="export-powerpoint-images-option"
                                verticalAlign="center"
                                horizontalAlign="left"
                                onClick={() => this.handleSetPptxEditable(false)}
                            >
                                <RadioSmall checked={!editableChecked} name="pptxMode" value="images" readOnly />
                                <InfoText><b>{t("Export as images.")}</b></InfoText>
                            </FlexBox>
                            <FlexBox
                                data-test-id="export-powerpoint-editable-option"
                                verticalAlign="center"
                                horizontalAlign="left"
                                onClick={() => this.handleSetPptxEditable(true)}
                            >
                                <RadioSmall checked={editableChecked} name="pptxMode" value="editable" readOnly />
                                <InfoText><b>{t("Export as editable.")}</b> {t("You may notice minor formatting differences.")}</InfoText>
                            </FlexBox>
                            {editableChecked && (
                                <CheckBoxContainer style={{ marginLeft: 28 }}>
                                    <CheckBox
                                        checked={downloadFonts}
                                        onChange={this.handleToggleDownloadFonts}
                                        label={<InfoText><b>{t("Download fonts.")}</b> {t("Install before opening your presentation.")}</InfoText>}
                                    />
                                </CheckBoxContainer>
                            )}
                        </StackedOptions>
                    </OptionBody>
                )}
            </OptionCard>
        );
    }

    renderGoogleSlidesOption() {
        if (!this.canExportEditable) {
            return null;
        }

        const { exportType } = this.state;
        const selected = exportType === "google-slides";

        return (
            <OptionCard $selected={selected} onClick={this.handleSelectGoogleSlides}>
                <OptionHeader>
                    <Icon blue={selected} size={22}>add_to_drive</Icon>
                    <OptionLabel>{t("Google Slides")}</OptionLabel>
                </OptionHeader>
            </OptionCard>
        );
    }

    renderOfflineOption() {
        if (!this.canExportOffline) {
            return null;
        }

        const { exportType } = this.state;
        const selected = exportType === "offline";

        return (
            <OptionCard $selected={selected} onClick={this.handleSelectOffline}>
                <OptionHeader>
                    <Icon blue={selected} size={22}>install_desktop</Icon>
                    <OptionLabel>{t("Offline Player")}</OptionLabel>
                </OptionHeader>
                {selected && (
                    <OptionBody>
                        <InfoText>
                            {t("Present without internet.")}{" "}
                            <a className="link-blue" onClick={this.downloadOfflinePlayer}>{t("Download player")}</a>
                        </InfoText>
                    </OptionBody>
                )}
            </OptionCard>
        );
    }

    render() {
        const { closeDialog } = this.props;
        const { exportType, includeSkippedSlides, copyingLink } = this.state;

        if (!this.exportAllowed) {
            return (
                <BeautifulDialog
                    maxWidth="sm"
                    closeDialog={closeDialog}
                >
                    <DialogTitleShort>
                        <div>{t("Export is not allowed for this workspace")}</div>
                    </DialogTitleShort>
                    <DialogActionsPadded>
                        <MuiButton onClick={closeDialog}>{t("Close")}</MuiButton>
                    </DialogActionsPadded>
                </BeautifulDialog>
            );
        }

        return (
            <BeautifulDialog
                maxWidth="sm"
                closeDialog={closeDialog}
            >
                <DialogTitleShort>
                    {t("Export presentation")}
                </DialogTitleShort>
                <DialogContentNoPad>
                    {this.shouldShowUpgradeBanner ? (
                        <ExportUpgradeBanner
                            headerText={t("Export without Beautiful.ai branding")}
                            bodyText={t("Upgrade to remove watermarks and export editable files.")}
                            buttonLabel={t("Upgrade")}
                            onButtonClick={this.handleUpgradeBannerClick}
                        />
                    ) : (
                        <ShareLinkBox>
                            <div className="share-link-text">
                                <div className="share-link-title">{t("Share a view link")}</div>
                                <div className="share-link-subtitle">
                                    {t("Shared links allow for animations, interactive content, and analytics to track views.")}
                                </div>
                            </div>
                            <Button blue onClick={this.handleCopyLink} disabled={copyingLink}>
                                <Icon white size={18}>link</Icon>
                                {t("Copy Link")}
                            </Button>
                        </ShareLinkBox>
                    )}

                    <ExportOptions>
                        {this.renderOfflineOption()}
                        {this.renderPdfOption()}
                        {this.renderPowerPointOption()}
                        {this.renderGoogleSlidesOption()}

                        <IncludeSkippedContainer>
                            <CheckBox
                                checked={includeSkippedSlides}
                                onChange={this.handleToggleIncludeSkippedSlides}
                                label={<InfoText><b>{t("Include skipped slides")}</b></InfoText>}
                            />
                        </IncludeSkippedContainer>
                    </ExportOptions>
                </DialogContentNoPad>
                <DialogActionsPadded>
                    <MuiButton onClick={closeDialog}>{t("Cancel")}</MuiButton>
                    <MuiButton onClick={this.handleAccept} color="primary" disabled={!exportType}>
                        {t("Export")}
                    </MuiButton>
                </DialogActionsPadded>
            </BeautifulDialog>
        );
    }
}
